import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { ApiClientConstant, ApiConnector, Models, Table } from '@cureskin/api-client';
import { ConnectionService } from '../../../../services/connection-service';
import { RegimenService } from '../../../../services/regimen-service';
import { Broadcaster } from '../../../../components/broadcaster';
import { AppConfig } from '../../../app.config';

type RegimenClass = Models.RegimenClass;

interface CheckoutOrderPayload extends Record<string, unknown> {
  addressBookId: string;
  paymentType: string;
  regimenId: string;
  type: 'PRODUCT';
  uniqueId: string;
  userObjectId: string;
  products: string;
  services: string;
  regimenReorderDetails: { class: string; enabled: boolean };
}

@Component({
    selector: 'regimen-reorder',
    templateUrl: './regimen-reorder.html',
    styleUrls: ['./regimen-reorder.scss'],
    standalone: true,
    imports: [CommonModule],
})
export class RegimenReorderComponent implements OnInit, OnDestroy {
  @Input() user: Table.User;

  activeRegimens: Array<Table.Regimen> = [];
  isExpanded: boolean = false;
  isCreating: boolean = false;
  private subscription?: Subscription;

  constructor(
    private readonly conn: ConnectionService,
    private readonly regimenService: RegimenService,
    private readonly broadcaster: Broadcaster,
    private readonly appConfig: AppConfig,
  ) {}

  ngOnInit(): void {
    this.subscription = this.regimenService.regimens$.subscribe(
      (regimens: Array<Table.Regimen>) => {
        this.activeRegimens = regimens.filter((regimen: Table.Regimen) => regimen.get('active') === true);
      },
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private extractErrorMessage(error: unknown): string {
    const axiosMessage: string | undefined = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
    if (axiosMessage) return axiosMessage;
    if (error instanceof Error) return error.message;
    return String(error);
  }

  async createInitialOrder(regimen: Table.Regimen): Promise<void> {
    if (this.isCreating) return;
    this.isCreating = true;
    try {
      const regimenClass: RegimenClass = regimen.get('class') as RegimenClass;

      // Step 1: eligibility check.
      let productIds: Array<string>;
      try {
        const response: { data?: { productIds?: Array<string> } } = await this.conn
          .getRegimenReorderCreationEligibility(this.user.id, regimenClass);
        const eligibilityProductIds: Array<string> | undefined = response.data?.productIds;
        if (!eligibilityProductIds?.length) {
          this.broadcaster.broadcast('NOTIFY', {
            message: 'Regimen reorder is not eligible — no products returned.',
            type: this.appConfig.Shared.Toast.Type.ERROR,
          });
          return;
        }
        productIds = eligibilityProductIds;
      } catch (error: unknown) {
        this.broadcaster.broadcast('NOTIFY', {
          message: this.extractErrorMessage(error),
          type: this.appConfig.Shared.Toast.Type.ERROR,
        });
        return;
      }

      // Step 2: default address (server-side filter).
      const addresses: Array<Table.AddressBook> = await this.conn.findAddressBook({
        where: { user: this.user, default: true },
      });
      if (!addresses.length) {
        this.broadcaster.broadcast('NOTIFY', {
          message: 'No default address found for user.',
          type: this.appConfig.Shared.Toast.Type.ERROR,
        });
        return;
      }
      const defaultAddress: Table.AddressBook = addresses[0];

      // Step 3: create the initial order.
      const payload: CheckoutOrderPayload = {
        addressBookId: defaultAddress.id,
        paymentType: ApiClientConstant.Order.PaymentType.NOT_SELECTED,
        regimenId: '',
        type: 'PRODUCT',
        uniqueId: uuid(),
        userObjectId: this.user.id,
        products: productIds.join(','),
        services: '',
        regimenReorderDetails: { class: regimenClass, enabled: true },
      };
      try {
        await ApiConnector.cloudRun('checkoutAndCreateOrder', payload);
      } catch (error: unknown) {
        this.broadcaster.broadcast('NOTIFY', {
          message: this.extractErrorMessage(error),
          type: this.appConfig.Shared.Toast.Type.ERROR,
        });
        return;
      }

      this.broadcaster.broadcast('NOTIFY', {
        message: 'Initial order created successfully.',
      });
    } finally {
      this.isCreating = false;
    }
  }
}
